import React, { useCallback, useContext, useEffect, useState } from "react"

import { AppContext } from "../App"
import { LetterStatus } from "../helpers"
import Key from "./Key"
import strings from "../strings"

const keys1 = ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"]
const keys2 = ["A", "S", "D", "F", "G", "H", "J", "K", "L"]
const keys3 = ["Z", "X", "C", "V", "B", "N", "M"]
const keys = [...keys1, ...keys2, ...keys3]

function Keyboard() {
  const {
    onDelete,
    onEnter,
    onSelectLetter,
    board,
    boardStatus,
    letterStatus,
  } = useContext(AppContext)

  // State to track pulse triggers for each key
  const [pulseTriggers, setPulseTriggers] = useState<Map<string, number>>(new Map())

  // Function to trigger pulse animation for a specific key
  const triggerKeyPulse = useCallback((keyVal: string) => {
    setPulseTriggers(prev => {
      const newMap = new Map(prev)
      newMap.set(keyVal, Date.now()) // Use timestamp to ensure uniqueness
      return newMap
    })
  }, [])

  const handleKeyboard = useCallback(
    (event: KeyboardEvent) => {
      // ignore composed events involving meta or ctrl
      if (event.metaKey || event.ctrlKey) {
        return
      }

      switch (event.key) {
        case "Enter":
          triggerKeyPulse(strings.enter)
          return onEnter()
        case "Backspace":
          triggerKeyPulse(strings.delete)
          return onDelete()
        default:
          keys.forEach((key) => {
            if (key === event.key.toUpperCase()) {
              triggerKeyPulse(key)
              return onSelectLetter(key)
            }
          })
      }
    },
    [onDelete, onEnter, onSelectLetter, triggerKeyPulse]
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyboard)

    return () => {
      document.removeEventListener("keydown", handleKeyboard)
    }
  })

  return (
    <div className="keyboard" data-testid="keyboard">
      <div className="line1">
        {keys1.map((key) => {
          return (
            <Key
              key={key}
              keyVal={key}
              status={letterStatus.get(key) || LetterStatus.Unknown}
              pulseTrigger={pulseTriggers.get(key)}
            />
          )
        })}
      </div>
      <div className="line2">
        {keys2.map((key) => {
          return (
            <Key
              key={key}
              keyVal={key}
              status={letterStatus.get(key) || LetterStatus.Unknown}
              pulseTrigger={pulseTriggers.get(key)}
            />
          )
        })}
      </div>
      <div className="line3">
        <Key 
          keyVal={strings.enter} 
          bigKey 
          pulseTrigger={pulseTriggers.get(strings.enter)}
        />
        {keys3.map((key) => {
          return (
            <Key
              key={key}
              keyVal={key}
              status={letterStatus.get(key) || LetterStatus.Unknown}
              pulseTrigger={pulseTriggers.get(key)}
            />
          )
        })}
        <Key 
          keyVal={strings.delete} 
          bigKey 
          pulseTrigger={pulseTriggers.get(strings.delete)}
        />
      </div>
    </div>
  )
}

export default Keyboard
